import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { formatCurrency } from '../utils/currency';
import { Plus, User, XCircle, KeyRound, Users, DollarSign, AlertCircle, FileText, ArrowLeft, Edit3, Download, Printer, CheckCircle, RefreshCw, Home } from 'lucide-react';
import { PageHeader, Card, StatTile, Badge, EmptyState, DataTable, Toolbar } from '../components/ui';
import ChargeBreakdown from '../components/charges/ChargeBreakdown';

const statusTone = s => ({ active: 'success', inactive: 'neutral', evicted: 'danger' }[s] || 'neutral');

const Tenants = () => {
    const [tenants, setTenants] = useState([]);
    const [properties, setProperties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [search, setSearch] = useState('');
    const [selectedTenant, setSelectedTenant] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const [t, p] = await Promise.all([
                axios.get('/api/tenants'),
                axios.get('/api/properties').catch(() => ({ data: { properties: [] } })),
            ]);
            setTenants(t.data.tenants || []);
            setProperties(p.data.properties || []);
        } catch { toast.error('Failed to load tenants'); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const [issuedPw, setIssuedPw] = useState(null);

    // Issues a fresh temporary password so a tenant who lost theirs can get back in
    // without a database edit. Shown once — it is not stored anywhere in plaintext.
    const resetPassword = async (t) => {
        if (!window.confirm(`Issue a new temporary password for ${t.first_name} ${t.last_name}?\n\nTheir current password stops working immediately.`)) return;
        try {
            const { data } = await axios.post(`/api/users/${t.user_id}/reset-password`);
            setIssuedPw({ name: `${t.first_name} ${t.last_name}`, password: data.temp_password });
        } catch (err) {
            toast.error(err.response?.data?.message || 'Could not reset password');
        }
    };

    const endLease = async id => {
        if (!window.confirm('End this lease?')) return;
        try { await axios.post(`/api/tenants/${id}/end-lease`); toast.success('Lease ended'); load(); }
        catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    };

    const active = tenants.filter(t => t.status === 'active');
    const totalOwed = tenants.reduce((s,t) => s + Number(t.balance || 0), 0);
    const totalDeposits = tenants.reduce((s,t) => s + Number(t.deposit_paid || 0), 0);
    
    const filtered = tenants.filter(t => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (`${t.first_name} ${t.last_name}`.toLowerCase().includes(q)
            || t.email?.toLowerCase().includes(q)
            || t.property_name?.toLowerCase().includes(q)
            || t.phone?.toLowerCase().includes(q));
    });

    if (selectedTenant) {
        return <TenantStatement tenant={selectedTenant} onBack={() => { setSelectedTenant(null); load(); }} />;
    }

    return (
        <div className="animate-fade-in">
            <PageHeader
                title="Tenants"
                subtitle="Active leases and past renters."
                actions={
                    <button onClick={() => setShowForm(true)} className="btn-primary">
                        <Plus className="h-4 w-4" /> New tenant
                    </button>
                }
            />

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
                <StatTile label="Total tenants"    value={tenants.length} icon={Users} accent="primary" />
                <StatTile label="Active"           value={active.length}  icon={User}  accent="success" />
                <StatTile label="Total rent"       value={formatCurrency(active.reduce((s,t) => s + Number(t.monthly_rent||0), 0))}
                          icon={DollarSign} accent="primary" hint="Monthly billable" />
                <StatTile label="Total deposits held" value={formatCurrency(totalDeposits)} icon={Home} accent="primary" hint={`Outstanding: ${formatCurrency(totalOwed)}`} />
            </div>

            <Card padding={false}>
                <div className="p-4 pb-0">
                    <Toolbar search={search} onSearch={setSearch} placeholder="Search by name, email, phone, property…" />
                </div>
                {loading ? (
                    <div className="p-8 text-neutral-400 text-sm text-center">Loading…</div>
                ) : filtered.length === 0 && !search ? (
                    <div className="p-8">
                        <EmptyState icon={User}
                            title="No tenants yet"
                            description="Adding a tenant creates their login and starts monthly rent invoicing automatically."
                            action={<button onClick={() => setShowForm(true)} className="btn-primary">
                                <Plus className="h-4 w-4" /> Add tenant
                            </button>} />
                    </div>
                ) : (
                    <DataTable
                        columns={[
                            { key: 'name', label: 'Tenant', sortable: true,
                              sortValue: r => `${r.first_name} ${r.last_name}`,
                              render: r => (
                                  <div className="cursor-pointer hover:text-primary-600" onClick={() => setSelectedTenant(r)}>
                                      <div className="font-medium text-neutral-900">{r.first_name} {r.last_name}</div>
                                      <div className="text-xs text-neutral-500">{r.email}</div>
                                  </div>
                              ) },
                            { key: 'property_name', label: 'Property', sortable: true },
                            { key: 'phone', label: 'Phone' },
                            { key: 'lease', label: 'Lease',
                              sortValue: r => r.lease_start_date,
                              render: r => (
                                  <div className="text-xs text-neutral-600 tabular-nums">
                                      {new Date(r.lease_start_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                      <span className="text-neutral-400"> → </span>
                                      {new Date(r.lease_end_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                  </div>
                              ) },
                            { key: 'monthly_rent', label: 'Monthly Rent', align: 'right', sortable: true, render: r => formatCurrency(r.monthly_rent) },
                            { key: 'total_paid', label: 'Paid Rent', align: 'right', sortable: true, render: r => formatCurrency(r.total_paid || 0) },
                            { key: 'total_billed', label: 'Expected', align: 'right', sortable: true, render: r => formatCurrency(r.total_billed || 0) },
                            { key: 'deposit_paid', label: 'Deposit', align: 'right', sortable: true, render: r => formatCurrency(r.deposit_paid || 0) },
                            { key: 'balance', label: 'Outstanding', align: 'right', sortable: true,
                              render: r => Number(r.balance) > 0
                                  ? <span className="text-danger-700 font-medium cursor-pointer hover:underline" onClick={() => setSelectedTenant(r)}>{formatCurrency(r.balance)}</span>
                                  : <span className="text-success-700">Clear</span> },
                            { key: 'status', label: 'Status',
                              render: r => <Badge tone={statusTone(r.status)} dot>{r.status}</Badge> },
                            { key: 'action', label: '',
                              render: r => (
                                  <div className="flex items-center gap-1">
                                      <button onClick={() => resetPassword(r)} title="Issue new password"
                                              className="text-neutral-500 hover:text-primary-700 hover:bg-primary-50 p-1 rounded">
                                          <KeyRound className="h-4 w-4" />
                                      </button>
                                      {r.status === 'active' && (
                                          <button onClick={() => endLease(r.id)} title="End lease"
                                                  className="text-danger-600 hover:bg-danger-50 p-1 rounded">
                                              <XCircle className="h-4 w-4" />
                                          </button>
                                      )}
                                  </div>
                              ) },
                        ]}
                        rows={filtered}
                        rowKey={r => r.id}
                        pageSize={20}
                        emptyMessage={search ? 'No tenants match your search.' : 'No tenants yet.'}
                    />
                )}
            </Card>

            {issuedPw && (
                <Modal title="New temporary password" onClose={() => setIssuedPw(null)}>
                    <div className="space-y-4">
                        <p className="text-sm text-neutral-600">
                            Give this to <span className="font-medium text-neutral-900">{issuedPw.name}</span>.
                            It is shown once and cannot be retrieved again — their previous password no longer works.
                        </p>
                        <div className="flex items-center gap-2">
                            <code className="flex-1 rounded-lg border border-neutral-200 bg-neutral-50 p-3 font-mono text-sm break-all">
                                {issuedPw.password}
                            </code>
                            <button type="button" className="btn-secondary shrink-0"
                                onClick={() => {
                                    navigator.clipboard?.writeText(issuedPw.password)
                                        .then(() => toast.success('Copied'))
                                        .catch(() => toast.error('Copy failed — select it manually'));
                                }}>
                                Copy
                            </button>
                        </div>
                        <p className="text-xs text-neutral-500">
                            Ask them to change it under Settings once they sign in.
                        </p>
                        <div className="flex justify-end">
                            <button type="button" className="btn-primary" onClick={() => setIssuedPw(null)}>Done</button>
                        </div>
                    </div>
                </Modal>
            )}

            {showForm && <NewTenantForm
                properties={properties.filter(p => p.status !== 'unavailable')}
                onClose={() => setShowForm(false)}
                onSaved={() => { setShowForm(false); load(); }} />}
        </div>
    );
};

const TenantStatement = ({ tenant, onBack }) => {
    const [statement, setStatement] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showEdit, setShowEdit] = useState(false);
    const [showNotice, setShowNotice] = useState(false);
    const [showRefund, setShowRefund] = useState(false);
    // `tenants` is a tenancy table - one row per person per unit - so this
    // statement only ever covers ONE unit. Count the person's tenancies so a
    // multi-unit tenant can be offered the combined view.
    const [unitCount, setUnitCount] = useState(1);
    const [showCombined, setShowCombined] = useState(false);
    const statementRef = useRef(null);

    const load = async () => {
        setLoading(true);
        try {
            const r = await axios.get(`/api/tenants/${tenant.id}/statement`);
            setStatement(r.data);
            if (tenant.user_id) {
                axios.get(`/api/tenants/by-user/${tenant.user_id}/statement`)
                    .then((c) => setUnitCount((c.data.units || []).length || 1))
                    .catch(() => { /* single-unit view is the safe default */ });
            }
        } catch (err) {
            toast.error('Failed to load statement');
            onBack();
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, [tenant.id, onBack]);

    const exportToPDF = async () => {
        if (!statementRef.current) return;
        try {
            const script = document.createElement('script');
            script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
            script.onload = () => {
                const element = statementRef.current;
                const opt = {
                    margin: 10,
                    filename: `${tenant.first_name}_${tenant.last_name}_statement.pdf`,
                    image: { type: 'jpeg', quality: 0.98 },
                    html2canvas: { scale: 2 },
                    jsPDF: { orientation: 'portrait', unit: 'mm', format: 'a4' }
                };
                window.html2pdf().set(opt).from(element).save();
                toast.success('PDF downloaded');
            };
            document.head.appendChild(script);
        } catch (err) {
            toast.error('Failed to generate PDF');
        }
    };

    const printStatement = () => {
        window.print();
    };

    if (showCombined) {
        return (
            <CombinedStatement
                userId={tenant.user_id}
                personName={`${tenant.first_name} ${tenant.last_name}`}
                onBack={() => setShowCombined(false)}
            />
        );
    }

    if (loading || !statement) {
        return <div className="p-8 text-center">Loading…</div>;
    }

    const { invoices, payments, financials } = statement;
    const noticeGiven = !!financials.notice_to_vacate_date;

    return (
        <div className="animate-fade-in">
            <div className="mb-4">
                <button onClick={onBack} className="text-xs text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1">
                    <ArrowLeft className="h-3 w-3" /> Back to tenants
                </button>
            </div>

            <PageHeader
                title={`${tenant.first_name} ${tenant.last_name}`}
                subtitle={`${tenant.property_name} · ${tenant.address}, ${tenant.city}`}
                actions={
                    <div className="flex flex-wrap gap-2">
                        <button onClick={printStatement} className="btn-secondary">
                            <Printer className="h-4 w-4" /> Print
                        </button>
                        <button onClick={exportToPDF} className="btn-secondary">
                            <Download className="h-4 w-4" /> PDF
                        </button>
                        {!noticeGiven && (
                            <button onClick={() => setShowNotice(true)} className="btn-secondary">
                                <CheckCircle className="h-4 w-4" /> Notice to vacate
                            </button>
                        )}
                        {noticeGiven && financials.deposit_held > 0 && !financials.refund_status && (
                            <button onClick={() => setShowRefund(true)} className="btn-secondary">
                                <RefreshCw className="h-4 w-4" /> Process refund
                            </button>
                        )}
                        {unitCount > 1 && (
                            <button onClick={() => setShowCombined(true)} className="btn-secondary">
                                <Home className="h-4 w-4" /> All {unitCount} units
                            </button>
                        )}
                        <button onClick={() => setShowEdit(true)} className="btn-secondary">
                            <Edit3 className="h-4 w-4" /> Edit tenant
                        </button>
                    </div>
                }
            />

            <div ref={statementRef} className="print:bg-white print:text-black">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
                    <StatTile label="Lease Period"
                        value={new Date(tenant.lease_start_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                        icon={FileText} accent="primary"
                        hint={`to ${new Date(tenant.lease_end_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`} />
                    <StatTile label="Monthly Rent" value={formatCurrency(tenant.monthly_rent)} icon={DollarSign} accent="primary" />
                    <StatTile label="Status" value={tenant.status} icon={User} accent={tenant.status === 'active' ? 'success' : 'neutral'} />
                    <StatTile label="Tenant Since" value={new Date(tenant.lease_start_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} icon={FileText} accent="primary" />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
                    <div className="p-4 bg-primary-50 rounded-lg">
                        <p className="text-xs text-neutral-500">Total Billed</p>
                        <p className="text-xl font-semibold text-primary-700">{formatCurrency(financials.total_billed)}</p>
                    </div>
                    <div className="p-4 bg-success-50 rounded-lg">
                        <p className="text-xs text-neutral-500">Total Paid</p>
                        <p className="text-xl font-semibold text-success-700">{formatCurrency(financials.total_paid)}</p>
                    </div>
                    <div className="p-4 bg-warning-50 rounded-lg">
                        <p className="text-xs text-neutral-500">Outstanding</p>
                        <p className="text-xl font-semibold text-warning-700">{formatCurrency(financials.outstanding)}</p>
                    </div>
                    <div className="p-4 bg-danger-50 rounded-lg">
                        <p className="text-xs text-neutral-500">Overdue</p>
                        <p className="text-xl font-semibold text-danger-700">{formatCurrency(financials.overdue)}</p>
                    </div>
                </div>

                {/* Rent vs water vs electricity vs garbage — billed, paid, owed. */}
                <div className="mb-6">
                    <ChargeBreakdown scope="tenant" id={tenant.id} title="Charges for this tenancy" />
                </div>

                {/* DEPOSITS SECTION */}
                <Card title="Deposits" padding={false} className="mb-6">
                    <div className="p-4 space-y-3">
                        <div className="flex justify-between">
                            <span className="text-sm text-neutral-600">Deposit Held</span>
                            <span className="font-medium text-neutral-900">{formatCurrency(financials.deposit_held)}</span>
                        </div>
                        {noticeGiven && (
                            <>
                                <div className="flex justify-between">
                                    <span className="text-sm text-neutral-600">Notice to Vacate Date</span>
                                    <span className="font-medium text-neutral-900">{new Date(financials.notice_to_vacate_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                                </div>
                                <div className="flex justify-between pt-2 border-t border-neutral-200">
                                    <span className="text-sm text-neutral-600">Applied to Outstanding</span>
                                    <span className="font-medium text-success-700">{formatCurrency(financials.deposit_applied)}</span>
                                </div>
                            </>
                        )}
                        {financials.refund_status && (
                            <div className="mt-3 p-3 bg-neutral-50 rounded border border-neutral-200">
                                <p className="text-xs font-medium text-neutral-600 mb-2">Refund Status</p>
                                <div className="space-y-1 text-xs">
                                    <div className="flex justify-between">
                                        <span>Deposit Amount:</span>
                                        <span>{formatCurrency(financials.refund_status.deposit_amount)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>Repairs/Deductions:</span>
                                        <span>-{formatCurrency(financials.refund_status.repairs_deductions)}</span>
                                    </div>
                                    <div className="flex justify-between font-medium pt-1 border-t border-neutral-200">
                                        <span>Refund Amount:</span>
                                        <span>{formatCurrency(financials.refund_status.refund_amount)}</span>
                                    </div>
                                    <p className="text-neutral-500 mt-2">Status: <Badge tone={financials.refund_status.status === 'processed' ? 'success' : 'neutral'} dot>{financials.refund_status.status}</Badge></p>
                                </div>
                            </div>
                        )}
                    </div>
                </Card>

                {/* INVOICES */}
                {invoices && invoices.length > 0 && (
                    <Card title="Recent Invoices" subtitle={`Last ${invoices.length}`} padding={false} className="mb-6">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-neutral-50 border-b border-neutral-200">
                                    <tr>
                                        <th className="py-2 px-3 text-left text-neutral-600 font-medium">Invoice #</th>
                                        <th className="py-2 px-3 text-left text-neutral-600 font-medium">Type</th>
                                        <th className="py-2 px-3 text-right text-neutral-600 font-medium">Amount</th>
                                        <th className="py-2 px-3 text-right text-neutral-600 font-medium">Paid</th>
                                        <th className="py-2 px-3 text-left text-neutral-600 font-medium">Due Date</th>
                                        <th className="py-2 px-3 text-left text-neutral-600 font-medium">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoices.map(inv => (
                                        <tr key={inv.id} className="border-b border-neutral-100 last:border-b-0 hover:bg-neutral-50">
                                            <td className="py-2 px-3 font-medium">{inv.invoice_number}</td>
                                            <td className="py-2 px-3 capitalize text-neutral-600">{inv.invoice_type}</td>
                                            <td className="py-2 px-3 text-right font-medium">{formatCurrency(inv.total_amount)}</td>
                                            <td className="py-2 px-3 text-right">{formatCurrency(inv.paid_amount)}</td>
                                            <td className="py-2 px-3 text-sm">{new Date(inv.due_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                                            <td className="py-2 px-3">
                                                <Badge tone={
                                                    inv.status === 'paid' ? 'success' :
                                                    inv.status === 'overdue' ? 'danger' :
                                                    'neutral'
                                                } dot>
                                                    {inv.status}
                                                </Badge>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Card>
                )}

                {/* PAYMENTS */}
                {payments && payments.length > 0 && (
                    <Card title="Recent Payments" subtitle={`Last ${payments.length}`} padding={false} className="mb-6">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-neutral-50 border-b border-neutral-200">
                                    <tr>
                                        <th className="py-2 px-3 text-left text-neutral-600 font-medium">Date</th>
                                        <th className="py-2 px-3 text-right text-neutral-600 font-medium">Amount</th>
                                        <th className="py-2 px-3 text-left text-neutral-600 font-medium">Method</th>
                                        <th className="py-2 px-3 text-left text-neutral-600 font-medium">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {payments.map(p => (
                                        <tr key={p.id} className="border-b border-neutral-100 last:border-b-0 hover:bg-neutral-50">
                                            <td className="py-2 px-3">{new Date(p.payment_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                                            <td className="py-2 px-3 text-right font-medium">{formatCurrency(p.amount)}</td>
                                            <td className="py-2 px-3 capitalize">{p.payment_method}</td>
                                            <td className="py-2 px-3">
                                                <Badge tone={p.status === 'completed' ? 'success' : 'neutral'} dot>
                                                    {p.status}
                                                </Badge>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Card>
                )}
            </div>

            {showEdit && <EditTenantForm tenant={tenant} onClose={() => setShowEdit(false)} onSaved={() => { setShowEdit(false); load(); }} />}
            {showNotice && <NoticeToVacateForm tenant={tenant} onClose={() => setShowNotice(false)} onSaved={() => { setShowNotice(false); load(); }} />}
            {showRefund && <RefundForm tenant={tenant} deposit={financials.deposit_held} onClose={() => setShowRefund(false)} onSaved={() => { setShowRefund(false); load(); }} />}

            <style>{`
                @media print {
                    body { margin: 0; padding: 10mm; }
                    .print\\:bg-white { background: white !important; }
                    .print\\:text-black { color: black !important; }
                    .btn-primary, .btn-secondary, .btn-tertiary, button { display: none !important; }
                    .animate-fade-in, .animate-slide-up { animation: none !important; }
                    .hover\\:*, [class*="hover:"] { color: inherit !important; background: inherit !important; }
                    table { border-collapse: collapse; }
                    th, td { border: 1px solid #ddd; padding: 8px; }
                    th { background-color: #f5f5f5; }
                }
            `}</style>
        </div>
    );
};

/**
 * One statement covering every unit a person holds: a section per unit, a
 * combined total at the bottom. The per-tenancy statement above stays as it
 * is - deposits, notice to vacate and refunds all belong to a single lease,
 * not to the person.
 */
const CombinedStatement = ({ userId, personName, onBack }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const ref = useRef(null);

    useEffect(() => {
        let alive = true;
        axios.get(`/api/tenants/by-user/${userId}/statement`)
            .then((r) => { if (alive) setData(r.data); })
            .catch(() => { toast.error('Failed to load combined statement'); onBack(); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [userId, onBack]);

    const exportToPDF = () => {
        if (!ref.current) return;
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
        script.onload = () => {
            window.html2pdf().set({
                margin: 10,
                filename: `${personName.replace(/\s+/g, '_')}_all_units_statement.pdf`,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2 },
                jsPDF: { orientation: 'portrait', unit: 'mm', format: 'a4' },
            }).from(ref.current).save();
            toast.success('PDF downloaded');
        };
        script.onerror = () => toast.error('Could not load the PDF library');
        document.head.appendChild(script);
    };

    if (loading) return <div className="p-8 text-center">Loading…</div>;
    if (!data) return null;

    const { person, units, totals, generated_at } = data;
    const d = (v) => (v ? new Date(v).toLocaleDateString('en-GB',
        { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

    return (
        <div className="animate-fade-in">
            <div className="mb-4 print:hidden">
                <button onClick={onBack} className="text-xs text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1">
                    <ArrowLeft className="h-3 w-3" /> Back to this unit
                </button>
            </div>

            <PageHeader
                title={`${person.first_name} ${person.last_name}`}
                subtitle={`Statement across ${totals.unit_count} unit${totals.unit_count === 1 ? '' : 's'} · ${d(generated_at)}`}
                actions={
                    <div className="flex flex-wrap gap-2 print:hidden">
                        <button onClick={() => window.print()} className="btn-secondary">
                            <Printer className="h-4 w-4" /> Print
                        </button>
                        <button onClick={exportToPDF} className="btn-secondary">
                            <Download className="h-4 w-4" /> PDF
                        </button>
                    </div>
                }
            />

            <div ref={ref} className="print:bg-white print:text-black space-y-6">
                {units.map((u) => (
                    <Card key={u.tenancy_id} title={u.unit} padding={false}
                          className="break-inside-avoid">
                        <div className="p-4 space-y-4">
                            <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-neutral-500">
                                {u.account_number && <span>Ref <span className="font-mono">{u.account_number}</span></span>}
                                <span>Rent {formatCurrency(u.monthly_rent)}/month</span>
                                <span>Lease {d(u.lease_start_date)} – {d(u.lease_end_date)}</span>
                                {u.status !== 'active' && <span className="text-warning-700">{u.status}</span>}
                            </div>

                            <div className="grid grid-cols-3 gap-3">
                                <div className="p-3 bg-primary-50 rounded-lg">
                                    <p className="text-xs text-neutral-500">Billed</p>
                                    <p className="text-lg font-semibold text-primary-700">{formatCurrency(u.summary.billed)}</p>
                                </div>
                                <div className="p-3 bg-success-50 rounded-lg">
                                    <p className="text-xs text-neutral-500">Paid</p>
                                    <p className="text-lg font-semibold text-success-700">{formatCurrency(u.summary.paid)}</p>
                                </div>
                                <div className="p-3 bg-warning-50 rounded-lg">
                                    <p className="text-xs text-neutral-500">Balance</p>
                                    <p className="text-lg font-semibold text-warning-700">{formatCurrency(u.summary.outstanding)}</p>
                                </div>
                            </div>

                            {u.invoices.length > 0 && (
                                <div>
                                    <p className="text-xs font-medium text-neutral-500 mb-1">Charges</p>
                                    <table className="w-full text-sm">
                                        <tbody>
                                            {u.invoices.slice(0, 12).map((i) => (
                                                <tr key={i.id} className="border-t">
                                                    <td className="py-1">{d(i.due_date)}</td>
                                                    <td className="py-1">{i.invoice_type}</td>
                                                    <td className="py-1 font-mono text-xs">{i.invoice_number}</td>
                                                    <td className="py-1 text-right">{formatCurrency(i.total_amount)}</td>
                                                    <td className="py-1 text-right text-neutral-500">
                                                        {formatCurrency(Number(i.total_amount) - Number(i.paid_amount))} due
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {u.payments.length > 0 && (
                                <div>
                                    <p className="text-xs font-medium text-neutral-500 mb-1">Payments</p>
                                    <table className="w-full text-sm">
                                        <tbody>
                                            {u.payments.slice(0, 12).map((pay) => (
                                                <tr key={pay.id} className="border-t">
                                                    <td className="py-1">{d(pay.payment_date)}</td>
                                                    <td className="py-1">{pay.payment_method}</td>
                                                    <td className="py-1 font-mono text-xs">{pay.receipt_number || pay.reference_number || ''}</td>
                                                    <td className="py-1 text-right">{formatCurrency(pay.amount)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {u.invoices.length === 0 && u.payments.length === 0 && (
                                <p className="text-sm text-neutral-500">Nothing billed or paid on this unit yet.</p>
                            )}
                        </div>
                    </Card>
                ))}

                <Card title="Combined total" padding={false} className="break-inside-avoid">
                    <div className="p-4">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div className="p-4 bg-neutral-50 rounded-lg">
                                <p className="text-xs text-neutral-500">Units</p>
                                <p className="text-xl font-semibold">{totals.unit_count}</p>
                            </div>
                            <div className="p-4 bg-primary-50 rounded-lg">
                                <p className="text-xs text-neutral-500">Total billed</p>
                                <p className="text-xl font-semibold text-primary-700">{formatCurrency(totals.billed)}</p>
                            </div>
                            <div className="p-4 bg-success-50 rounded-lg">
                                <p className="text-xs text-neutral-500">Total paid</p>
                                <p className="text-xl font-semibold text-success-700">{formatCurrency(totals.paid)}</p>
                            </div>
                            <div className="p-4 bg-warning-50 rounded-lg">
                                <p className="text-xs text-neutral-500">Total outstanding</p>
                                <p className="text-2xl font-bold text-warning-700">{formatCurrency(totals.outstanding)}</p>
                            </div>
                        </div>
                        <p className="text-xs text-neutral-500 mt-3">
                            Combined rent {formatCurrency(totals.monthly_rent)} per month across active units.
                            Each unit is paid against its own reference; payments are applied to the
                            oldest unpaid charge on that unit first.
                        </p>
                    </div>
                </Card>
            </div>
        </div>
    );
};

const NoticeToVacateForm = ({ tenant, onClose, onSaved }) => {
    const today = new Date().toISOString().slice(0, 10);
    const [noticeDate, setNoticeDate] = useState(today);
    const [saving, setSaving] = useState(false);

    const submit = async e => {
        e.preventDefault();
        setSaving(true);
        try {
            await axios.post(`/api/tenants/${tenant.id}/notice-to-vacate`, { notice_date: noticeDate });
            toast.success('Notice to vacate recorded');
            onSaved();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed');
            setSaving(false);
        }
    };

    return (
        <Modal title={`Notice to Vacate — ${tenant.first_name} ${tenant.last_name}`} onClose={onClose}>
            <form onSubmit={submit}>
                <div className="p-5 space-y-4">
                    <p className="text-sm text-neutral-600">This records that the tenant has given notice to vacate. Their deposit can now be offset against outstanding rent.</p>
                    <Field label="Notice Date">
                        <input required type="date" className="input" value={noticeDate} onChange={e => setNoticeDate(e.target.value)} />
                    </Field>
                </div>
                <div className="p-5 border-t border-neutral-100 flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
                    <button disabled={saving} className="btn-primary">{saving ? 'Saving…' : 'Record notice'}</button>
                </div>
            </form>
        </Modal>
    );
};

const RefundForm = ({ tenant, deposit, onClose, onSaved }) => {
    const [repairs, setRepairs] = useState(0);
    const [notes, setNotes] = useState('');
    const [saving, setSaving] = useState(false);
    const refundAmount = Math.max(0, deposit - Number(repairs));

    const submit = async e => {
        e.preventDefault();
        setSaving(true);
        try {
            await axios.post(`/api/tenants/${tenant.id}/refund`, {
                repairs_deductions: Number(repairs),
                notes
            });
            toast.success(`Refund processed: ${formatCurrency(refundAmount)}`);
            onSaved();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed');
            setSaving(false);
        }
    };

    return (
        <Modal title={`Process Deposit Refund — ${tenant.first_name} ${tenant.last_name}`} onClose={onClose}>
            <form onSubmit={submit}>
                <div className="p-5 space-y-4">
                    <div className="bg-primary-50 border border-primary-100 rounded-lg p-3 text-sm text-primary-800">
                        <p className="font-medium mb-1">Deposit Held: {formatCurrency(deposit)}</p>
                        <p className="text-sm">After repairs: {formatCurrency(refundAmount)}</p>
                    </div>
                    <Field label="Repairs/Deductions (KES)">
                        <input type="number" step="0.01" className="input" value={repairs} onChange={e => setRepairs(e.target.value)} />
                    </Field>
                    <Field label="Notes">
                        <textarea rows={2} className="input" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Description of repairs, damages, etc." />
                    </Field>
                </div>
                <div className="p-5 border-t border-neutral-100 flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
                    <button disabled={saving} className="btn-primary">{saving ? 'Processing…' : 'Process refund'}</button>
                </div>
            </form>
        </Modal>
    );
};

const EditTenantForm = ({ tenant, onClose, onSaved }) => {
    const [f, setF] = useState({
        lease_start_date: tenant.lease_start_date,
        lease_end_date: tenant.lease_end_date,
        monthly_rent: tenant.monthly_rent,
        deposit_paid: tenant.deposit_paid || '',
        status: tenant.status,
    });
    const [saving, setSaving] = useState(false);
    const set = (k, v) => setF(x => ({ ...x, [k]: v }));

    const submit = async e => {
        e.preventDefault();
        setSaving(true);
        try {
            await axios.put(`/api/tenants/${tenant.id}`, f);
            toast.success('Tenant updated');
            onSaved();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to update');
            setSaving(false);
        }
    };

    return (
        <Modal title={`Edit ${tenant.first_name} ${tenant.last_name}`} onClose={onClose}>
            <form onSubmit={submit}>
                <div className="p-5 grid grid-cols-2 gap-3">
                    <Field label="Lease start"><input required type="date" className="input" value={f.lease_start_date} onChange={e => set('lease_start_date', e.target.value)} /></Field>
                    <Field label="Lease end"><input required type="date" className="input" value={f.lease_end_date} onChange={e => set('lease_end_date', e.target.value)} /></Field>
                    <Field label="Monthly rent (KES)"><input required type="number" step="0.01" className="input" value={f.monthly_rent} onChange={e => set('monthly_rent', e.target.value)} /></Field>
                    <Field label="Deposit paid (KES)"><input type="number" step="0.01" className="input" value={f.deposit_paid} onChange={e => set('deposit_paid', e.target.value)} /></Field>
                    <Field label="Status" span2>
                        <select className="input" value={f.status} onChange={e => set('status', e.target.value)}>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                            <option value="evicted">Evicted</option>
                        </select>
                    </Field>
                </div>
                <div className="p-5 border-t border-neutral-100 flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
                    <button disabled={saving} className="btn-primary">{saving ? 'Saving…' : 'Save changes'}</button>
                </div>
            </form>
        </Modal>
    );
};

const NewTenantForm = ({ properties, onClose, onSaved }) => {
    const today = new Date().toISOString().slice(0, 10);
    const oneYear = new Date(); oneYear.setFullYear(oneYear.getFullYear() + 1);
    const [f, setF] = useState({
        first_name:'', last_name:'', email:'', phone:'',
        property_id:'', lease_start_date: today,
        lease_end_date: oneYear.toISOString().slice(0, 10),
        monthly_rent:'', deposit_paid:'', rent_due_day: 5,
    });
    const [saving, setSaving] = useState(false);
    const [tempPw, setTempPw] = useState(null);
    const set = (k, v) => setF(x => ({ ...x, [k]: v }));

    const onPropChange = (id) => {
        set('property_id', id);
        const p = properties.find(p => String(p.id) === String(id));
        if (p) {
            if (!f.monthly_rent) set('monthly_rent', p.rent_amount);
            if (!f.deposit_paid) set('deposit_paid', p.deposit_amount || '');
        }
    };

    const submit = async e => {
        e.preventDefault(); setSaving(true);
        try {
            const { data } = await axios.post('/api/tenants', f);
            if (data.temp_password) setTempPw(data.temp_password);
            else { toast.success('Tenant added'); onSaved(); }
        } catch (err) { toast.error(err.response?.data?.message || 'Failed'); setSaving(false); }
    };

    if (tempPw) return (
        <Modal title="Tenant added — share this login" onClose={onSaved}>
            <div className="p-5 space-y-3">
                <p className="text-sm text-neutral-600">They can log in with their email and this temporary password:</p>
                <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-3 font-mono text-sm">{tempPw}</div>
                <div className="text-right pt-2"><button onClick={onSaved} className="btn-primary">Done</button></div>
            </div>
        </Modal>
    );

    return (
        <Modal title="New tenant" onClose={onClose}>
            <form onSubmit={submit}>
                <div className="p-5 grid grid-cols-2 gap-3">
                    <Field label="First name"><input required className="input" value={f.first_name} onChange={e => set('first_name', e.target.value)} /></Field>
                    <Field label="Last name"><input required className="input" value={f.last_name} onChange={e => set('last_name', e.target.value)} /></Field>
                    <Field label="Email"><input required type="email" className="input" value={f.email} onChange={e => set('email', e.target.value)} /></Field>
                    <Field label="Phone"><input className="input" placeholder="+254…" value={f.phone} onChange={e => set('phone', e.target.value)} /></Field>
                    <Field label="Property" span2>
                        <select required className="input" value={f.property_id} onChange={e => onPropChange(e.target.value)}>
                            <option value="">Select property…</option>
                            {properties.map(p => (
                                <option key={p.id} value={p.id}>
                                    {p.house_number ? `#${p.house_number} ` : ''}{p.name} — {p.building_display_name || p.building_name || p.city}
                                </option>
                            ))}
                        </select>
                    </Field>
                    <Field label="Lease start"><input required type="date" className="input" value={f.lease_start_date} onChange={e => set('lease_start_date', e.target.value)} /></Field>
                    <Field label="Lease end"><input required type="date" className="input" value={f.lease_end_date} onChange={e => set('lease_end_date', e.target.value)} /></Field>
                    <Field label="Monthly rent (KES)"><input required type="number" step="0.01" className="input" value={f.monthly_rent} onChange={e => set('monthly_rent', e.target.value)} /></Field>
                    <Field label="Deposit paid (KES)"><input type="number" step="0.01" className="input" value={f.deposit_paid} onChange={e => set('deposit_paid', e.target.value)} /></Field>
                    <Field label="Rent due day of month"><input type="number" min="1" max="28" className="input" value={f.rent_due_day} onChange={e => set('rent_due_day', e.target.value)} /></Field>
                </div>
                <div className="p-5 border-t border-neutral-100 flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
                    <button disabled={saving} className="btn-primary">{saving?'Saving…':'Create tenant'}</button>
                </div>
            </form>
        </Modal>
    );
};

const Modal = ({ title, children, onClose }) => (
    <div className="fixed inset-0 z-50 bg-neutral-900/40 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
        <div className="bg-white rounded-xl shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-slide-up" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-neutral-100 flex justify-between items-center">
                <h2 className="text-sm font-semibold text-neutral-900">{title}</h2>
                <button onClick={onClose} className="text-neutral-400 hover:text-neutral-700">✕</button>
            </div>
            {children}
        </div>
    </div>
);

const Field = ({ label, children, span2 }) => (
    <div className={span2 ? 'col-span-2' : ''}>
        <label className="block text-xs font-medium text-neutral-700 mb-1">{label}</label>{children}
    </div>
);

export default Tenants;
